import { v4 as uuidv4 } from 'uuid';

import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { ElementAddress, MediaImage } from 'editor/src/store/design/types';
import { getLinkedCollectionElements } from 'editor/src/store/design/util/collectionLink';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';
import { buildAdvPersoPluginsUpdate, getAdvPersoDataFromElement } from 'editor/src/store/plugins/utils/advPersoUtils';

import unlinkCollectionElementOperation from './unlinkCollectionElementOperation';

// Links two collection layers so they represent one customer choice: both elements get the same
// linkGroupId (joining the target's existing group if it has one, otherwise generating a new one),
// and the current element adopts the target's name since linked layers share a single name.
const linkCollectionElementsOperation =
  (currentAddress: ElementAddress, targetAddress: ElementAddress) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    let state = getState();
    let current = getMediaElement<MediaImage>(state, currentAddress);
    const target = getMediaElement<MediaImage>(state, targetAddress);
    let currentData = current && getAdvPersoDataFromElement(current);
    const targetData = target && getAdvPersoDataFromElement(target);
    if (!current || !target || !currentData || !targetData) {
      return;
    }

    // Re-linking: leave the old group cleanly first (including lone-member cleanup), then re-read
    // the element since the unlink rewrote its plugins.
    if (currentData.linkGroupId && currentData.linkGroupId !== targetData.linkGroupId) {
      dispatch(unlinkCollectionElementOperation(currentAddress));
      state = getState();
      current = getMediaElement<MediaImage>(state, currentAddress);
      currentData = current && getAdvPersoDataFromElement(current);
      if (!current || !currentData) {
        return;
      }
    }

    const linkGroupId = targetData.linkGroupId ?? uuidv4();

    dispatch(
      updateMediaElementAction({
        elementAddress: currentAddress,
        elementUpdate: {
          plugins: buildAdvPersoPluginsUpdate(current, { ...currentData, linkGroupId }),
          // Linked layers represent one customer choice, so they share one name.
          name: target.name,
        },
      }),
    );

    if (!targetData.linkGroupId) {
      dispatch(
        updateMediaElementAction({
          elementAddress: targetAddress,
          elementUpdate: { plugins: buildAdvPersoPluginsUpdate(target, { ...targetData, linkGroupId }) },
        }),
      );
    }

    // Linked layers share one display mode: the group adopts the primary's (design order,
    // first member) displayOption, so joining a group can flip the newcomer's mode. The mock
    // store's getState() doesn't reflect dispatches made within this same operation, so the
    // post-link group is built from data already in hand (state read at the top of this
    // operation, plus current/target's post-link linkGroupId) rather than re-reading state.
    const preExistingGroup = targetData.linkGroupId
      ? getLinkedCollectionElements(state.design.designData!, targetData.linkGroupId)
      : [];
    const isSameAddress = (a: ElementAddress, b: ElementAddress) =>
      a.spreadIndex === b.spreadIndex && a.pageIndex === b.pageIndex && a.elementIndex === b.elementIndex;

    const group = [
      ...preExistingGroup.filter(
        (member) => !isSameAddress(member.address, currentAddress) && !isSameAddress(member.address, targetAddress),
      ),
      { element: target, collectionInfo: { ...targetData, linkGroupId }, address: targetAddress },
      { element: current, collectionInfo: { ...currentData, linkGroupId }, address: currentAddress },
    ].sort((a, b) => {
      if (a.address.spreadIndex !== b.address.spreadIndex) {
        return a.address.spreadIndex - b.address.spreadIndex;
      }
      if (a.address.pageIndex !== b.address.pageIndex) {
        return a.address.pageIndex - b.address.pageIndex;
      }
      return a.address.elementIndex - b.address.elementIndex;
    });

    const primaryDisplayOption = group[0]?.collectionInfo.displayOption;
    if (primaryDisplayOption !== undefined) {
      group.forEach((member) => {
        if (member.collectionInfo.displayOption !== primaryDisplayOption) {
          dispatch(
            updateMediaElementAction({
              elementAddress: member.address,
              elementUpdate: {
                plugins: buildAdvPersoPluginsUpdate(member.element, {
                  ...member.collectionInfo,
                  displayOption: primaryDisplayOption,
                }),
              },
            }),
          );
        }
      });
    }
  };

export default linkCollectionElementsOperation;
